import CardButton from '@/common/components/cardButton/CardButton';
import MainLayout from '@/common/components/layouts/MainLayout';
import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { css } from 'styled-system/css';
import { Box } from 'styled-system/jsx';
import CircularLoading from '@/common/components/base/Loading/CircularLoading';
import { AdjusterImageTabType } from '@/modules/adjusterReportModules/IncidentParticipants/types/images';
import ComplimentImagesIcon from '@/modules/adjusterReportModules/IncidentParticipants/assets/icons/complimentImagesIcon.svg?react';
import DocumentImagesIcon from '@/modules/adjusterReportModules/IncidentParticipants/assets/icons/documentImagesIcon.svg?react';
import VehicleImagesIcon from '@/modules/adjusterReportModules/IncidentParticipants/assets/icons/vehicleImagesIcon.svg?react';
import DamagedImagesIcon from '@/modules/adjusterReportModules/IncidentParticipants/assets/icons/damagedImagesIcon.svg?react';
import { useQuery } from '@tanstack/react-query';
import TitleDescription from '@/common/components/base/typography/TitleDescription';
import { getCulpritAdjusterImagesTabsOptions } from '@/modules/adjusterReportModules/IncidentParticipants/queries/getCulpritAdjusterImagesTabs/getCulpritAdjusterImagesTabsOptions';
import getCulpritAdjusterImageTabsAlertsQueryOptions from '@/modules/adjusterReportModules/IncidentParticipants/queries/getCulpritAdjusterImageTabsAlerts/getCulpritAdjusterImageTabsAlertsQueryOptions';
import { getDamagedAdjusterImagesQueryOptions } from '@/modules/adjusterReportModules/IncidentParticipants/queries/getDamagedAdjusterImages/getDamagedAdjusterImagesQueryOptions';
import { useImageFlowStore } from '@/modules/adjusterReportModules/IncidentParticipants/store/imageFlow/useImageFlowStore';
import useDamagedPoints from '@/modules/adjusterReportModules/IncidentParticipants/store/damagePoints/useDamagedPoints';
import useGetOfflineImages from '@/modules/OfflineFiles/hooks/useGetOfflineImages';
import { getHasOfflineShootingQueryOptions } from '@/modules/OfflineFiles/queries/getHasOfflineShooting/getHasOfflineShootingQueryOptions';
import OfflineShowPhotoInformationModal from '@/modules/OfflineFiles/components/modals/OfflineShowPhotoInformationModal';
import useModal from '@/common/hooks/useModal';
import { ADJUSTER_REPORT_CULPRIT_LINKS } from '@/modules/adjusterReportModules/adjusterReport/links/culprit/culpritLinks';

export default () => {
  return (
    <MainLayout title=" تصاویر / مقصر " header="titleHeader">
      <CulpritAdjusterImagesTabs />
    </MainLayout>
  );
};

const CulpritAdjusterImagesTabs = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { closeModal, open, openModal } = useModal();

  const { data } = useQuery(
    getCulpritAdjusterImagesTabsOptions({
      id: +id!,
    }),
  );
  const { data: offlineShootingData } = useQuery(
    getHasOfflineShootingQueryOptions({
      id: id!,
      userType: 'culprit',
    }),
  );
  const { resetCameraStore } = useImageFlowStore();
  const { resetDamagedPoints } = useDamagedPoints();
  const { getCurrentFileOfflineImages } = useGetOfflineImages();

  const { data: culpritVehicleImages } = useQuery(
    getDamagedAdjusterImagesQueryOptions({
      id: +id!,
      enable: true,
      participantRoleType: 'culprit',
    }),
  );

  const { isPending } = useQuery(
    getCulpritAdjusterImageTabsAlertsQueryOptions({
      id: +id!,
    }),
  );
  const renderRoute = async (item: AdjusterImageTabType) => {
    const editable = item.editable;
    let hasOffline = false;
    if (
      item.name === 'vehicle_image' &&
      offlineShootingData?.has_offline_shooting
    ) {
      hasOffline = (await getCurrentFileOfflineImages({})).hasOffline;
      if (!culpritVehicleImages?.vehicle.length && !hasOffline) {
        return openModal();
      }
    }
    let tabIcon: Record<AdjusterImageTabType['name'], string> = {
      additional_image: editable
        ? ADJUSTER_REPORT_CULPRIT_LINKS.CULPRIT_IMAGES({
            id: +id!,
          }).ADJUSTER_ADDITIONAL.edit
        : ADJUSTER_REPORT_CULPRIT_LINKS.CULPRIT_IMAGES({
            id: +id!,
          }).ADJUSTER_ADDITIONAL.show,
      document_image: editable
        ? ADJUSTER_REPORT_CULPRIT_LINKS.CULPRIT_IMAGES({
            id: +id!,
          }).ADJUSTER_DOCUMENT.edit
        : ADJUSTER_REPORT_CULPRIT_LINKS.CULPRIT_IMAGES({
            id: +id!,
          }).ADJUSTER_DOCUMENT.show,
      vehicle_image:
        culpritVehicleImages?.vehicle.length === 0 && !hasOffline
          ? ADJUSTER_REPORT_CULPRIT_LINKS.CULPRIT_IMAGES({
              id: +id!,
            }).ADJUSTER_VEHICLE.damagedPoints
          : editable
            ? ADJUSTER_REPORT_CULPRIT_LINKS.CULPRIT_IMAGES({
                id: +id!,
              }).ADJUSTER_VEHICLE.previewImageDamagePoint
            : ADJUSTER_REPORT_CULPRIT_LINKS.CULPRIT_IMAGES({
                id: +id!,
              }).ADJUSTER_VEHICLE.showPreviewImages,
      damaged_image: '',
    };
    return tabIcon[item.name];
  };
  const goToFile = async (item: AdjusterImageTabType) => {
    const path = await renderRoute(item);
    if (path) {
      return navigate(path);
    }
  };
  useEffect(() => {
    resetDamagedPoints();
    resetCameraStore();
  }, []);
  return (
    <>
      <Box pt="2rem" mt="4rem">
        {isPending ? (
          <Box display="flex" alignItems="center" justifyContent="center">
            <CircularLoading color="colors.purple-primary" />
          </Box>
        ) : (
          <Box
            className={css({
              display: 'flex',
              flexDirection: 'column',
              gap: '24px',
              position: 'relative',
              top: '-5px',
            })}
          >
            <Box
              className={css({
                display: 'flex',
                flexDirection: 'column',
                gap: '24px',
              })}
            >
              <TitleDescription>تصاویر کارشناس</TitleDescription>
              <Box>
                {data?.map((item, index) =>
                  (item.name === 'vehicle_image' ||
                    item.name === 'damaged_image') &&
                  !item.is_card_active ? null : (
                    <CardButton
                      key={item?.name}
                      cardProps={{
                        className: css({
                          marginY: '1rem',
                          marginTop: index === 0 ? '0rem' : '1rem',
                          marginBottom:
                            index === data.length - 1 ? '0rem' : '1rem',
                          cursor: item?.editable ? 'pointer' : 'not-allowed',
                        }),
                        onClick: () => goToFile(item),
                      }}
                      hasArrow
                      renderIcon={() => renderIcon(item?.name)}
                      title={item?.fa_name}
                    />
                  ),
                )}
              </Box>
            </Box>
          </Box>
        )}
      </Box>
      <OfflineShowPhotoInformationModal
        confirmButtonText="شروع فرایند عکس برداری"
        open={open}
        closeModal={() => {
          closeModal();
          navigate(
            ADJUSTER_REPORT_CULPRIT_LINKS.CULPRIT_IMAGES({
              id: +id!,
            }).ADJUSTER_VEHICLE.damagedPoints,
          );
        }}
      />
    </>
  );
};

const renderIcon = (tabName: AdjusterImageTabType['name']) => {
  let tabIcon: Record<AdjusterImageTabType['name'], React.JSX.Element> = {
    additional_image: <ComplimentImagesIcon />,
    document_image: <DocumentImagesIcon />,
    vehicle_image: <VehicleImagesIcon />,
    damaged_image: <DamagedImagesIcon />,
  };
  return tabIcon[tabName];
};
